/**
 * Carrier portali (eskiz 28): carrier o'ziga topshirilgan nomzodlarni ko'radi, qabul qiladi yoki rad etadi.
 * Alohida sodda shell; sahifalar URL bilan boshqariladi (/portal, /portal/jobs, /portal/account).
 * Carrier SSN, tug'ilgan sana, ichki izohlar va boshqa carrier'lardagi natijalarni ko'rmaydi.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Briefcase, LogOut, Search, UserRound, Users } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { cn, formatDate } from "@/lib/utils";
import { useLogout, useMe } from "@/features/auth/hooks";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ThemeToggle } from "@/components/ThemeToggle";
import { BrandLockup } from "@/components/brand/BrandLogo";
import { ContextMenu } from "@/components/ui/context-menu";
import { callPhone, copyText } from "@/lib/clipboard";
import { portalCandidateMenu, portalJobMenu } from "@/features/portal/menu";
import { useProfileLanguage } from "@/features/auth/language";
import { CountBadge, StatusPill } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { Field, Input, Select, Textarea } from "@/components/ui/field";
import { Avatar, EmptyState, ErrorState, Kv, PageLoading } from "@/components/ui/misc";
import { Modal } from "@/components/ui/modal";
import { FilterChips, Table, Td, Th, Tr } from "@/components/ui/table";
import { toast } from "@/components/ui/toast";

interface PortalMe {
  carrier: { id: number; name: string; mc_number: string };
  company_name: string;
  user: { id: number; display_name: string; initials: string; username: string };
}

type PortalStatus = "new" | "in_review" | "accepted" | "rejected";
type Filter = "all" | PortalStatus;

interface Candidate {
  id: number;
  driver_id: number;
  full_name: string;
  job: { id: number; title: string };
  cdl_class: string;
  experience_months: number | null;
  state: string;
  submitted_at: string;
  status: PortalStatus;
  app_status: string;
  phone: string | null;
  cdl_expires: string | null;
  medical_card_expires: string | null;
  endorsements: string[];
  equipment: string[];
  note: string;
  carrier_reply: string;
  rejection_reason: string;
  version: number;
}

interface PortalJob {
  id: number;
  title: string;
  status: string;
  states: string[];
  openings: number;
  pay_text: string;
  home_time: string;
  equipment: string;
  candidates: number;
  hired: number;
  in_review: number;
}

const FILTERS: Filter[] = ["all", "new", "in_review", "accepted", "rejected"];
// value — serverga yuboriladigan matn (inglizcha, o'zgarmas); label — tarjima kaliti portal:panel.reasons.<label>
const REJECT_REASONS: { value: string; label: string }[] = [
  { value: "MVR / driving record", label: "mvr" },
  { value: "Experience", label: "experience" },
  { value: "Drug test", label: "drug" },
  { value: "PSP / crash history", label: "psp" },
  { value: "No-show", label: "noshow" },
  { value: "Other", label: "other" },
];
const portalKey = ["portal"] as const;

type T = (key: string, opts?: Record<string, unknown>) => string;

function expLabel(t: T, months: number | null): string {
  if (months === null || months === undefined) return t("exp.none");
  if (months < 12) return t("exp.months", { n: months });
  return t("exp.years", { count: Math.round(months / 12) });
}

function statesLabel(t: T, states: string[]): string {
  if (states.includes("ALL")) return t("allStates");
  return states.join(", ") || "—";
}

export function CarrierPortalPage() {
  const { t } = useTranslation("portal");
  const me = useQuery({ queryKey: [...portalKey, "me"], queryFn: () => api.get<PortalMe>("/api/portal/me/"), retry: false });
  // Carrier foydalanuvchisining profil tili (xodimlar paneli bilan bir xil qoida)
  const profile = useMe();
  useProfileLanguage(profile.data?.language);
  const logout = useLogout();
  const { pathname } = useLocation();
  const view: "candidates" | "jobs" | "account" = pathname.startsWith("/portal/jobs") ? "jobs" : pathname.startsWith("/portal/account") ? "account" : "candidates";
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");

  // Qidiruv 300 ms kechikish bilan serverga yuboriladi
  useEffect(() => {
    const tm = window.setTimeout(() => setQ(search.trim()), 300);
    return () => window.clearTimeout(tm);
  }, [search]);

  const candidates = useQuery({
    queryKey: [...portalKey, "candidates", q],
    queryFn: () => api.get<{ results: Candidate[]; counts: Record<string, number> }>("/api/portal/candidates/", { query: { q } }),
    enabled: !!me.data,
    staleTime: 15_000,
  });
  const newCount = useMemo(() => (candidates.data?.results ?? []).filter((c) => c.status === "new").length, [candidates.data]);

  if (me.isPending) return <PageLoading />;
  if (!me.data) {
    return (
      <div className="mx-auto max-w-md p-8">
        <ErrorState message={me.error instanceof ApiError ? me.error.message : t("accessOnly")} />
        <Button className="mt-4" onClick={() => logout.mutate()}>
          {t("signOut")}
        </Button>
      </div>
    );
  }
  const info = me.data;

  const menu = [
    { to: "/portal", label: t("nav.candidates"), icon: Users, active: view === "candidates", badge: newCount },
    { to: "/portal/jobs", label: t("nav.jobs"), icon: Briefcase, active: view === "jobs", badge: 0 },
    { to: "/portal/account", label: t("nav.account"), icon: UserRound, active: view === "account", badge: 0 },
  ];

  return (
    <div className="flex min-h-screen">
      <aside className="flex w-[200px] shrink-0 flex-col border-r border-border bg-sidebar">
        <div className="px-4 pt-4 pb-3">
          <BrandLockup height={23} />
          <div className="mt-3 text-[13px] font-semibold">{t("title")}</div>
          <div className="truncate text-[11px] text-muted" title={info.carrier.name}>
            {info.carrier.name}
          </div>
        </div>
        <nav className="flex flex-col gap-0.5 px-3">
          {menu.map((m) => (
            <Link
              key={m.to}
              to={m.to}
              className={cn("flex items-center gap-2 rounded-[10px] px-3 py-2 text-[13px] text-ink-soft hover:bg-fill", m.active && "bg-primary-soft font-semibold text-ink")}
            >
              <m.icon size={14} />
              {m.label}
              {m.badge > 0 && <CountBadge count={m.badge} />}
            </Link>
          ))}
        </nav>
        <div className="mt-auto px-5 py-4 text-[11px] text-muted">{t("poweredBy", { company: info.company_name })}</div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 items-center gap-4 border-b border-line bg-surface px-5">
          <div className="relative w-[360px] max-w-full">
            <Search size={14} className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-muted" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("searchPlaceholder")} className="pl-8" aria-label={t("searchPlaceholder")} />
          </div>
          <div className="ml-auto flex items-center gap-3">
            <ThemeToggle />
            <LanguageSwitcher />
            <div className="flex items-center gap-2">
              <Avatar initials={info.user.initials} name={info.user.display_name} />
              <span className="leading-tight">
                <span className="block text-[12px] font-semibold">{info.user.display_name}</span>
                <span className="block text-[11px] text-muted">{info.carrier.name}</span>
              </span>
            </div>
            <Button size="sm" variant="ghost" onClick={() => logout.mutate()} loading={logout.isPending}>
              <LogOut size={14} /> {t("logout")}
            </Button>
          </div>
        </header>
        <main className="flex-1 overflow-x-hidden px-5 py-4">
          {view === "candidates" && <CandidatesView info={info} data={candidates.data} isPending={candidates.isPending} error={candidates.error} onRetry={() => candidates.refetch()} q={q} />}
          {view === "jobs" && <JobsView />}
          {view === "account" && <AccountView info={info} onLogout={() => logout.mutate()} />}
        </main>
      </div>
    </div>
  );
}

function CandidatesView({ info, data, isPending, error, onRetry, q }: { info: PortalMe; data?: { results: Candidate[]; counts: Record<string, number> }; isPending: boolean; error: Error | null; onRetry: () => void; q: string }) {
  const { t } = useTranslation("portal");
  const [filter, setFilter] = useState<Filter>("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  // Menyudan "Accept…/Reject…": panel ochiladi va kerakli oyna darhol chiqadi
  const [panelAction, setPanelAction] = useState<{ action: "accept" | "reject"; nonce: number } | null>(null);
  const { t: tm } = useTranslation("menu");
  const candidateItems = (c: Candidate) =>
    portalCandidateMenu(
      c,
      {
        details: () => setSelectedId(c.id),
        accept: () => {
          setSelectedId(c.id);
          setPanelAction({ action: "accept", nonce: Date.now() });
        },
        reject: () => {
          setSelectedId(c.id);
          setPanelAction({ action: "reject", nonce: Date.now() });
        },
        call: callPhone,
        copy: (text) => void copyText(text),
      },
      tm,
    );
  const results = useMemo(() => data?.results ?? [], [data]);
  // Sonlar filtrsiz ro'yxatdan hisoblanadi, shuning uchun filtr mijoz tomonda
  const counts = useMemo(() => {
    const c: Record<Filter, number> = { all: results.length, new: 0, in_review: 0, accepted: 0, rejected: 0 };
    for (const r of results) c[r.status] += 1;
    return c;
  }, [results]);
  const rows = useMemo(() => (filter === "all" ? results : results.filter((r) => r.status === filter)), [results, filter]);
  const selected = results.find((r) => r.id === selectedId) ?? null;

  return (
    <div>
      <div className="mb-4">
        <h1 className="text-[24px] leading-tight font-bold">{t("candidates.title")}</h1>
        <div className="mt-1 text-[13px] text-muted">{t("candidates.subtitle", { carrier: info.carrier.name, company: info.company_name })}</div>
      </div>
      <div className="mb-3">
        <FilterChips items={FILTERS.map((f) => ({ key: f, label: f === "all" ? t("all") : t(`status.${f}`), count: counts[f] }))} value={filter} onChange={setFilter} />
      </div>
      {error ? (
        <ErrorState message={error instanceof ApiError ? error.message : t("candidates.loadFailed")} onRetry={onRetry} />
      ) : isPending ? (
        <PageLoading />
      ) : rows.length === 0 ? (
        <EmptyState title={t("candidates.emptyTitle")} description={q ? t("candidates.emptySearch", { q }) : t("candidates.emptyDesc")} />
      ) : (
        <div className="flex items-start gap-4">
          <div className="min-w-0 flex-1">
            <Table>
              <thead>
                <tr>
                  <Th>{t("candidates.col.driver")}</Th>
                  <Th>{t("candidates.col.job")}</Th>
                  <Th>{t("candidates.col.cdl")}</Th>
                  <Th>{t("candidates.col.state")}</Th>
                  <Th>{t("candidates.col.submitted")}</Th>
                  <Th>{t("candidates.col.status")}</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <ContextMenu key={c.id} items={() => candidateItems(c)}>
                    <Tr clickable onClick={() => setSelectedId(c.id)} className={cn(selectedId === c.id && "bg-primary-soft/40")} aria-selected={selectedId === c.id}>
                      <Td className="font-semibold">{c.full_name}</Td>
                      <Td>{c.job.title}</Td>
                      <Td>
                        {c.cdl_class || "—"} · {expLabel(t, c.experience_months)}
                      </Td>
                      <Td>{c.state || "—"}</Td>
                      <Td>{formatDate(c.submitted_at)}</Td>
                      <Td>
                        <StatusPill status={c.status} label={t(`status.${c.status}`)} />
                      </Td>
                    </Tr>
                  </ContextMenu>
                ))}
              </tbody>
            </Table>
            <div className="py-3 text-[12px] text-muted">{t("candidates.count", { shown: rows.length, total: results.length })}</div>
          </div>
          {selected && <CandidatePanel candidate={selected} onClose={() => setSelectedId(null)} autoAction={panelAction} />}
        </div>
      )}
    </div>
  );
}

function CandidatePanel({ candidate: c, onClose, autoAction }: { candidate: Candidate; onClose: () => void; autoAction?: { action: "accept" | "reject"; nonce: number } | null }) {
  const { t } = useTranslation("portal");
  const qc = useQueryClient();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [comment, setComment] = useState("");
  const [acceptComment, setAcceptComment] = useState("");
  const [acceptOpen, setAcceptOpen] = useState(false);
  // O'ng tugma menyusidan kelgan amal: tegishli oynani ochadi (har bosish yangi nonce)
  useEffect(() => {
    if (!autoAction) return;
    if (autoAction.action === "accept") setAcceptOpen(true);
    else setRejectOpen(true);
  }, [autoAction]);

  const decide = useMutation({
    mutationFn: ({ action, body }: { action: "accept" | "reject"; body: Record<string, string> }) => api.post<Candidate>(`/api/portal/candidates/${c.id}/${action}/`, body),
    onSuccess: (res, vars) => {
      qc.invalidateQueries({ queryKey: portalKey });
      toast.success(
        vars.action === "accept" ? t("panel.accepted", { name: res.full_name }) : t("panel.rejected", { name: res.full_name }),
        vars.action === "accept" ? t("panel.acceptedDesc") : t("panel.rejectedDesc"),
      );
      setRejectOpen(false);
      setAcceptOpen(false);
      setReason("");
      setComment("");
      setAcceptComment("");
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t("panel.decisionFailed")),
  });
  const decidable = c.status === "new" || c.status === "in_review";

  return (
    <aside className="w-[360px] shrink-0">
      <Card>
        <CardHeader
          title={
            <span className="flex items-center gap-2">
              {c.full_name}
              <StatusPill status={c.status} label={t(`status.${c.status}`)} />
            </span>
          }
          subtitle={t("panel.submitted", { date: formatDate(c.submitted_at), job: c.job.title })}
          action={
            <button onClick={onClose} className="text-[12px] text-muted hover:text-ink" aria-label={t("panel.close")}>
              ✕
            </button>
          }
        />
        <CardBody>
          <Kv label={t("panel.phone")}>{c.phone ?? <span className="text-muted">{t("panel.hidden")}</span>}</Kv>
          <Kv label={t("panel.state")}>{c.state || "—"}</Kv>
          <Kv label={t("panel.cdl")}>{c.cdl_class ? t("panel.class", { cls: c.cdl_class }) : "—"}</Kv>
          <Kv label={t("panel.cdlExpires")}>{c.cdl_expires ? formatDate(c.cdl_expires) : "—"}</Kv>
          <Kv label={t("panel.medical")}>{c.medical_card_expires ? t("panel.validUntil", { date: formatDate(c.medical_card_expires) }) : "—"}</Kv>
          <Kv label={t("panel.experience")}>{expLabel(t, c.experience_months)}</Kv>
          <Kv label={t("panel.endorsements")}>{c.endorsements.length ? c.endorsements.join(", ") : "—"}</Kv>
          <Kv label={t("panel.equipment")}>{c.equipment.length ? c.equipment.join(", ") : "—"}</Kv>
          <Kv label={t("panel.note")}>{c.note || <span className="text-muted">—</span>}</Kv>
          {c.carrier_reply && <Kv label={t("panel.yourReply")}>{c.carrier_reply}</Kv>}
          {c.rejection_reason && <Kv label={t("panel.rejectionReason")}>{c.rejection_reason}</Kv>}

          <div className="mt-3 border-t border-line pt-3">
            <div className="text-[12px] font-semibold">{t("panel.documents")}</div>
            <p className="mt-1 text-[12px] text-muted">{t("panel.documentsDesc")}</p>
          </div>
          <p className="mt-3 rounded-[10px] bg-fill/50 px-3 py-2 text-[11px] text-muted">{t("panel.notShared")}</p>

          {decidable && (
            <div className="mt-4 flex gap-2">
              <Button variant="primary" className="flex-1" onClick={() => setAcceptOpen(true)} loading={decide.isPending && decide.variables?.action === "accept"}>
                {t("panel.accept")}
              </Button>
              <Button variant="danger" className="flex-1" onClick={() => setRejectOpen(true)}>
                {t("panel.reject")}
              </Button>
            </div>
          )}
        </CardBody>
      </Card>

      <Modal
        open={acceptOpen}
        onOpenChange={setAcceptOpen}
        title={t("panel.acceptTitle", { name: c.full_name })}
        description={t("panel.acceptDesc")}
        size="sm"
        footer={
          <>
            <Button onClick={() => setAcceptOpen(false)}>{t("common:actions.cancel")}</Button>
            <Button variant="primary" loading={decide.isPending} onClick={() => decide.mutate({ action: "accept", body: { comment: acceptComment.trim() } })}>
              {t("panel.accept")}
            </Button>
          </>
        }
      >
        <Field label={t("panel.comment")} htmlFor="accept-comment">
          <Textarea id="accept-comment" value={acceptComment} onChange={(e) => setAcceptComment(e.target.value)} placeholder={t("panel.commentPlaceholder")} />
        </Field>
      </Modal>

      <Modal
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        title={t("panel.rejectTitle", { name: c.full_name })}
        description={t("panel.rejectDesc", { job: c.job.title })}
        size="sm"
        footer={
          <>
            <Button onClick={() => setRejectOpen(false)}>{t("common:actions.cancel")}</Button>
            <Button variant="danger" loading={decide.isPending} disabled={!reason} onClick={() => decide.mutate({ action: "reject", body: { reason, comment: comment.trim() } })}>
              {t("panel.confirmReject")}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Field label={t("panel.rejectReason")} required htmlFor="reject-reason">
            <Select id="reject-reason" value={reason} onChange={(e) => setReason(e.target.value)} autoFocus>
              <option value="">{t("panel.chooseReason")}</option>
              {REJECT_REASONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {t(`panel.reasons.${r.label}`)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t("panel.comment")} htmlFor="reject-comment">
            <Textarea id="reject-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t("panel.commentPlaceholder")} />
          </Field>
        </div>
      </Modal>
    </aside>
  );
}

function JobsView() {
  const { t } = useTranslation("portal");
  const { t: tm } = useTranslation("menu");
  const jobs = useQuery({ queryKey: [...portalKey, "jobs"], queryFn: () => api.get<PortalJob[]>("/api/portal/jobs/"), staleTime: 30_000 });
  return (
    <div>
      <div className="mb-4">
        <h1 className="text-[24px] leading-tight font-bold">{t("jobs.title")}</h1>
        <div className="mt-1 text-[13px] text-muted">{t("jobs.subtitle")}</div>
      </div>
      {jobs.error ? (
        <ErrorState message={jobs.error instanceof ApiError ? jobs.error.message : t("jobs.loadFailed")} onRetry={() => jobs.refetch()} />
      ) : jobs.isPending ? (
        <PageLoading />
      ) : jobs.data.length === 0 ? (
        <EmptyState title={t("jobs.emptyTitle")} description={t("jobs.emptyDesc")} />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>{t("jobs.col.job")}</Th>
              <Th>{t("jobs.col.status")}</Th>
              <Th>{t("jobs.col.states")}</Th>
              <Th>{t("jobs.col.openings")}</Th>
              <Th>{t("jobs.col.pay")}</Th>
              <Th>{t("jobs.col.homeTime")}</Th>
              <Th>{t("jobs.col.equipment")}</Th>
              <Th>{t("jobs.col.candidates")}</Th>
              <Th>{t("jobs.col.inReview")}</Th>
              <Th>{t("jobs.col.hired")}</Th>
            </tr>
          </thead>
          <tbody>
            {jobs.data.map((j) => (
              <ContextMenu key={j.id} items={() => portalJobMenu(j, (text) => void copyText(text), tm)}>
                <Tr>
                  <Td className="font-semibold">{j.title}</Td>
                  <Td>
                    <StatusPill status={j.status} />
                  </Td>
                  <Td>{statesLabel(t, j.states)}</Td>
                  <Td>{t("jobs.hiredOf", { hired: j.hired, openings: j.openings })}</Td>
                  <Td>{j.pay_text || "—"}</Td>
                  <Td>{j.home_time || "—"}</Td>
                  <Td>{j.equipment || "—"}</Td>
                  <Td>{j.candidates}</Td>
                  <Td>{j.in_review}</Td>
                  <Td>{j.hired}</Td>
                </Tr>
              </ContextMenu>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}

function AccountView({ info, onLogout }: { info: PortalMe; onLogout: () => void }) {
  const { t } = useTranslation("portal");
  return (
    <div className="max-w-[560px]">
      <div className="mb-4">
        <h1 className="text-[24px] leading-tight font-bold">{t("account.title")}</h1>
      </div>
      <Card>
        <CardHeader title={t("account.card")} />
        <CardBody>
          <Kv label={t("account.name")}>{info.user.display_name}</Kv>
          <Kv label={t("account.username")}>{info.user.username}</Kv>
          <Kv label={t("account.carrier")}>{info.carrier.name}</Kv>
          <Kv label={t("account.mc")}>{info.carrier.mc_number || "—"}</Kv>
          <p className="mt-3 text-[12px] text-muted">{t("account.contact")}</p>
          <div className="mt-4">
            <Button onClick={onLogout}>
              <LogOut size={14} /> {t("logout")}
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
